const API_BASE_URL =
  typeof process.env.REACT_APP_API_BASE_URL === 'string'
    ? process.env.REACT_APP_API_BASE_URL.trim()
    : '';

const normalizeBaseUrl = (baseUrl) => baseUrl.replace(/\/$/, '');

const buildUrl = (path) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (!API_BASE_URL) {
    return normalizedPath;
  }
  return `${normalizeBaseUrl(API_BASE_URL)}${normalizedPath}`;
};

const parseResponsePayload = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }
  return response.text();
};

const resolveErrorMessage = (payload, fallbackMessage) => {
  if (!payload) {
    return fallbackMessage;
  }
  if (typeof payload === 'string') {
    return payload || fallbackMessage;
  }
  return payload?.message || payload?.error || fallbackMessage;
};

const extractToken = (payload) => {
  if (!payload || typeof payload === 'string') {
    return '';
  }
  return (
    payload.token ||
    payload.accessToken ||
    payload?.data?.token ||
    payload?.data?.accessToken ||
    ''
  );
};

const extractUserProfile = (payload) => {
  if (!payload || typeof payload === 'string') {
    return null;
  }
  return payload.user || payload?.data?.user || null;
};

const normalizeRoles = (rawRoles) => {
  if (!rawRoles) {
    return [];
  }
  if (Array.isArray(rawRoles)) {
    return rawRoles.filter(Boolean).map((role) => String(role).trim());
  }
  if (typeof rawRoles === 'string') {
    return rawRoles
      .split(',')
      .map((role) => role.trim())
      .filter(Boolean);
  }
  return [];
};

const mapSchoolsList = (payload) => {
  if (Array.isArray(payload?.schools)) {
    return payload.schools;
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  return [];
};

export const loginRequest = async ({ userId, password }) => {
  const response = await fetch(buildUrl('/api/v1/auth/login'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      userId,
      password,
    }),
  });

  const payload = await parseResponsePayload(response);

  if (!response.ok) {
    throw new Error(
      resolveErrorMessage(payload, 'حدث خطأ أثناء محاولة تسجيل الدخول.')
    );
  }

  const token = extractToken(payload);

  if (!token) {
    throw new Error('لم يتم استلام رمز دخول من الخادم.');
  }

  const userProfile = extractUserProfile(payload);
  const roles = normalizeRoles(payload?.role || userProfile?.role);

  return {
    token,
    user: userProfile ? { ...userProfile, role: roles } : null,
    roles,
    raw: payload,
  };
};

export const logoutRequest = async (token) => {
  if (!token) {
    return;
  }
  const response = await fetch(buildUrl('/api/v1/auth/logout'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const payload = await parseResponsePayload(response);

  if (!response.ok) {
    throw new Error(
      resolveErrorMessage(payload, 'تعذر تسجيل الخروج من النظام.')
    );
  }

  return payload;
};

export const getSchoolsRequest = async (token) => {
  // استخدام endpoint الداشبورد لجلب المدارس
  const response = await fetch(buildUrl('/api/v1/nfc/check-cards/dashboard/summary'), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const payload = await parseResponsePayload(response);
  
  console.log('📊 Dashboard Response:', payload); // تشخيص

  if (!response.ok) {
    throw new Error(
      resolveErrorMessage(payload, 'تعذر جلب قائمة المدارس من الخادم.')
    );
  }

  // استخراج المدارس من استجابة الداشبورد
  if (payload && payload.schools && Array.isArray(payload.schools)) {
    const schools = payload.schools.map(school => ({
      schoolObjectId: school.schoolObjectId || school._id,
      schoolName: school.schoolName || '',
      schoolCode: school.schoolCode || '',
      groupName: school.groupName || '',
      totalCards: school.totalCards || 0,
      checkPrintingCard: school.checkPrintingCard || {},
      checkAddEmail: school.checkAddEmail || {},
      checkPaperCat: school.checkPaperCat || {},
      checkEndAll: school.checkEndAll || {},
    }));
    console.log('✅ Mapped Schools:', schools); // تشخيص
    return schools;
  }

  console.log('⚠️ Using mapSchoolsList fallback'); // تشخيص
  return mapSchoolsList(payload);
};

const mapSchoolFromPayload = (payload) => {
  if (!payload) {
    return null;
  }

  if (Array.isArray(payload.school) && payload.school.length > 0) {
    return payload.school[0];
  }

  return payload.school || null;
};

export const getSchoolUsersRequest = async ({ schoolId, token }) => {
  const response = await fetch(
    buildUrl(`/api/v1/nfc/check-cards/school/${schoolId}`),
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const payload = await parseResponsePayload(response);

  if (!response.ok) {
    throw new Error(
      resolveErrorMessage(payload, 'تعذر جلب مستخدمي المدرسة المختارة.')
    );
  }

  let users = [];
  if (Array.isArray(payload?.data)) {
    users = payload.data;
  } else if (Array.isArray(payload?.users)) {
    users = payload.users;
  } else if (Array.isArray(payload)) {
    users = payload;
  }

  return {
    users,
    school: mapSchoolFromPayload(payload),
    total: payload?.results ?? users.length,
  };
};

export const getSchoolManagersRequest = async ({ schoolId, token }) => {
  const response = await fetch(
    buildUrl(`/api/v1/nfc/check-cards/school/${schoolId}/managers`),
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const payload = await parseResponsePayload(response);

  if (!response.ok) {
    throw new Error(
      resolveErrorMessage(payload, 'تعذر جلب إداريي المدرسة المختارة.')
    );
  }

  let users = [];
  if (Array.isArray(payload?.data)) {
    users = payload.data;
  } else if (Array.isArray(payload?.users)) {
    users = payload.users;
  } else if (Array.isArray(payload)) {
    users = payload;
  }

  return {
    users,
    school: mapSchoolFromPayload(payload),
    total: payload?.results ?? users.length,
  };
};

export const confirmPrintingRequest = async ({
  recordId,
  token,
  printingDate,
}) => {
  const payloadBody = {
    printingDate: printingDate || new Date().toISOString(),
  };

  const response = await fetch(
    buildUrl(`/api/v1/nfc/check-cards/${recordId}/check-printing`),
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payloadBody),
    }
  );

  const payload = await parseResponsePayload(response);

  if (!response.ok) {
    throw new Error(
      resolveErrorMessage(payload, 'تعذر تأكيد طباعة بطاقة هذا المستخدم.')
    );
  }

  return payload;
};

export const updateUserNfcRequest = async ({ userId, nfcId, token }) => {
  const response = await fetch(buildUrl(`/api/v1/user/${userId}/nfcId`), {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ nfcId }),
  });

  const payload = await parseResponsePayload(response);

  if (!response.ok) {
    throw new Error(
      resolveErrorMessage(payload, 'تعذر تحديث معرف NFC للمستخدم.')
    );
  }

  return payload;
};

const getProgressRequestFactory = (pathSegment) => async (token) => {
  const response = await fetch(
    buildUrl(`/api/v1/nfc/check-cards/progress/${pathSegment}`),
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const payload = await parseResponsePayload(response);

  if (!response.ok) {
    throw new Error(
      resolveErrorMessage(payload, 'تعذر جلب تقدم الطباعة للمستخدم.')
    );
  }

  return payload?.data || [];
};

export const getPrintingProgressRequest =
  getProgressRequestFactory('printing');

export const getAddEmailProgressRequest =
  getProgressRequestFactory('add-email');

export const getPaperCatProgressRequest =
  getProgressRequestFactory('paper-cat');

export const getEndAllProgressRequest =
  getProgressRequestFactory('end-all');

const confirmStepRequest = async ({
  recordId,
  token,
  endpoint,
  dateKey,
  dateValue,
}) => {
  const options = {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  if (dateValue) {
    const keys = Array.isArray(dateKey) ? dateKey : [dateKey];
    const payload = keys.reduce((acc, key) => {
      if (typeof key === 'string' && key.trim().length > 0) {
        acc[key] = dateValue;
      }
      return acc;
    }, {});

    if (Object.keys(payload).length > 0) {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(payload);
    }
  }

  const response = await fetch(
    buildUrl(`/api/v1/nfc/check-cards/${recordId}/${endpoint}`),
    options
  );

  const payload = await parseResponsePayload(response);

  if (!response.ok) {
    throw new Error(resolveErrorMessage(payload, 'تعذر تحديث حالة المستخدم.'));
  }

  return payload;
};

export const confirmAddEmailRequest = async ({
  recordId,
  token,
  addEmailDate,
}) =>
  confirmStepRequest({
    recordId,
    token,
    endpoint: 'check-add-email',
    dateKey: 'AddEmailDate',
    dateValue: addEmailDate,
  });

export const confirmPaperCatRequest = async ({
  recordId,
  token,
  paperCatDate,
}) =>
  confirmStepRequest({
    recordId,
    token,
    endpoint: 'check-paper-cat',
    dateKey: 'paperCatDate',
    dateValue: paperCatDate,
  });

export const confirmEndAllRequest = async ({
  recordId,
  token,
  endCheckDate,
}) =>
  confirmStepRequest({
    recordId,
    token,
    endpoint: 'check-end-all',
    dateKey: ['endAllDate', 'endCheckDate'],
    dateValue: endCheckDate,
  });

export const getDashboardSummaryRequest = async (token) => {
  const response = await fetch(
    buildUrl('/api/v1/nfc/check-cards/dashboard/summary'),
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const payload = await parseResponsePayload(response);

  if (!response.ok) {
    throw new Error(
      resolveErrorMessage(payload, 'تعذر جلب ملخص لوحة التحكم.')
    );
  }

  return payload || {};
};

export const populateNfcCardsRequest = async (token) => {
  const response = await fetch(buildUrl('/api/v1/nfc/check-cards/populate'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const payload = await parseResponsePayload(response);

  if (!response.ok) {
    throw new Error(
      resolveErrorMessage(payload, 'حدث خطأ أثناء محاولة تحديث بيانات البطاقات.')
    );
  }

  return payload;
};

export const apiClient = {
  baseUrl: API_BASE_URL,
  buildUrl,
  loginRequest,
  logoutRequest,
  getSchoolsRequest,
  getSchoolUsersRequest,
  getSchoolManagersRequest,
  confirmPrintingRequest,
  updateUserNfcRequest,
  getPrintingProgressRequest,
  getDashboardSummaryRequest,
  getAddEmailProgressRequest,
  confirmAddEmailRequest,
  confirmPaperCatRequest,
  confirmEndAllRequest,
  getPaperCatProgressRequest,
  getEndAllProgressRequest,
  populateNfcCardsRequest,
};

